/**
 * encuadre.js — QUÉ PARTE de la imagen se ve dentro de su caja (D-1429 B, 26-10-2026).
 *
 * Pedido del dueño: «además de recortarla también me tiene que dejar encuadrarla y que se vea lo
 * que quiero de lo que recorté (como cuando recortas una imagen y la mueves)». La caja (su tamaño
 * lo da el motor de dimensión) se queda quieta; dentro, la imagen se MUEVE y se AMPLÍA.
 *
 * El dato es un FOCO en % y un zoom, no un desplazamiento en píxeles: así aguanta que la caja
 * cambie de proporción (móvil, otra columna) sin que el encuadre se vaya a otra parte.
 *   image_pos_x, image_pos_y  0..100 (50 = centrado)  → `object-position: X% Y%`
 *   image_zoom                100..300 (%)           → `scale: Z/100` con `transform-origin` en el foco
 * El punto (X%, Y%) de la imagen cae en el (X%, Y%) de la caja (así es `object-position`) y el zoom
 * gira alrededor de ese mismo punto: el foco no se mueve al ampliar.
 *
 * `scale` y no `transform`: las entradas animadas escriben `transform` EN LÍNEA en la imagen
 * (gotcha 46) y lo borrarían; la propiedad individual `scale` se compone con él.
 * Con zoom, la imagen crece más allá de su caja: el CSS (`site.css`, «ENCUADRE») hace que la caja
 * recorte (`overflow: hidden`) y, en «Tarjeta», le pasa a la caja el radio y la sombra.
 *
 * Sin ningún prop no se emite nada: el bloque sale byte a byte como siempre. Los números se
 * acotan y se redondean ANTES de ir a un `style=` (este atributo no pasa por ningún saneador).
 *
 * PURO (node, ES2019): lo usan los renders (content, hero v3, features, imagen y, POR ITEM, galería,
 * equipo y carrusel: D-1452) y el editor (editor/encuadre-lienzo.js, editor/imagen-estilo.js), que
 * arrastra con `focoDesdeArrastre` y amplía con `zoomDesdeRueda`. En un array el dato va en el
 * ITEM (`gallery_items[i].image_pos_x`…): `atributosEncuadre(item)` vale tal cual.
 */
import { admiteRecorte } from './imagen-recorte.js';

export var PROPS_ENCUADRE = ['image_pos_x', 'image_pos_y', 'image_zoom'];
export var ZOOM_MIN = 100;
export var ZOOM_MAX = 300;

function _num(v, min, max, defecto) {
    var n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN);
    if (!isFinite(n)) return defecto;
    return Math.min(max, Math.max(min, Math.round(n)));
}

/** {x, y, z} acotados, o null si el bloque no tiene ningún prop de encuadre. */
export function normalizarEncuadre(props) {
    var p = props || {};
    var hay = false;
    for (var i = 0; i < PROPS_ENCUADRE.length; i++) {
        if (p[PROPS_ENCUADRE[i]] !== undefined && p[PROPS_ENCUADRE[i]] !== null) hay = true;
    }
    if (!hay) return null;
    return {
        x: _num(p.image_pos_x, 0, 100, 50),
        y: _num(p.image_pos_y, 0, 100, 50),
        z: _num(p.image_zoom, ZOOM_MIN, ZOOM_MAX, ZOOM_MIN),
    };
}

/** Clase y estilo para el `<img>`: {clase: ' n3i-encuadre[ n3i-zoom]', estilo: ' object-position…'}. */
export function atributosEncuadre(props) {
    var e = normalizarEncuadre(props);
    if (!e) return { clase: '', estilo: '' };
    return {
        clase: ' n3i-encuadre' + (e.z > ZOOM_MIN ? ' n3i-zoom' : ''),
        estilo: estiloEncuadre(e),
    };
}

/** El estilo en línea de un encuadre ya normalizado. El pintado en vivo del editor
 *  (`editor/encuadre-lienzo.js::pintarEncuadreVivo`) escribe las mismas tres piezas. */
export function estiloEncuadre(e) {
    var s = ' object-position: ' + e.x + '% ' + e.y + '%;';
    if (e.z > ZOOM_MIN) {
        s += ' scale: ' + (e.z / 100) + '; transform-origin: ' + e.x + '% ' + e.y + '%;'
            + ' clip-path: ' + recorteDelZoom(e) + ';';
    }
    return s;
}

function _dos(v) {
    return Math.round(v * 100) / 100;
}

/**
 * Con zoom la imagen crece más allá de su caja, y la caja no siempre recorta: en el editor TODA
 * tarjeta de un array va con `overflow: visible !important` (editor.css, por sus «+» de fuera), así
 * que la foto de una celda de la galería tapaba a sus vecinas en el lienzo y en «Ver sitio». La
 * propia imagen se recorta a su caja: con el origen del zoom en (X %, Y %) y la escala z, el punto
 * `p` de la imagen se pinta en `o + z·(p − o)`; la caja [0, 100 %] es, en la imagen, [o·(1 − 1/z),
 * o + (100 % − o)/z]. El `clip-path` va ANTES de la escala, así que se escala con ella y cae justo
 * en el borde de la caja. Lo publicado ya recortaba (overflow de la celda): esto es un cinturón.
 */
export function recorteDelZoom(e) {
    var f = 1 - 100 / e.z;
    return 'inset(' + _dos(e.y * f) + '% ' + _dos((100 - e.x) * f) + '% ' + _dos((100 - e.y) * f) + '% ' + _dos(e.x * f) + '%)';
}

/**
 * Ancho y alto con los que se pinta la imagen SIN zoom dentro de su caja (`cover` o `contain`).
 * @param {{cajaAncho, cajaAlto, imgAncho, imgAlto, ajuste}} m  medidas en píxeles
 */
export function tamanoPintado(m) {
    if (!(m.imgAncho > 0) || !(m.imgAlto > 0) || !(m.cajaAncho > 0) || !(m.cajaAlto > 0)) return null;
    var ex = m.cajaAncho / m.imgAncho;
    var ey = m.cajaAlto / m.imgAlto;
    var e = m.ajuste === 'contain' ? Math.min(ex, ey) : Math.max(ex, ey);
    return { ancho: m.imgAncho * e, alto: m.imgAlto * e };
}

/**
 * El foco nuevo tras ARRASTRAR la imagen `dx`, `dy` píxeles (en pantalla): la imagen sigue al
 * puntero. Con la imagen pintada a `W·z` en una caja de `C`, el punto del foco está en `X·C` y un
 * punto `p` de la imagen en `X·C + W·z·(p − X)`; moverlo `dx` es `X' = X − dx / (W·z − C)`. En el eje
 * en el que la imagen no sobra ni falta (W·z ≈ C) no hay nada que mover. Acotado a 0..100: con
 * `cover` la imagen nunca deja hueco; con `contain`, nunca se sale de su caja.
 */
export function focoDesdeArrastre(e, dx, dy, m) {
    var t = tamanoPintado(m);
    if (!t) return { x: e.x, y: e.y };
    var z = e.z / 100;
    var mover = function (actual, d, pintado, caja) {
        var sobra = pintado * z - caja;
        if (Math.abs(sobra) < 0.5) return actual;
        return Math.min(100, Math.max(0, actual - (d / sobra) * 100));
    };
    return {
        x: Math.round(mover(e.x, dx, t.ancho, m.cajaAncho) * 10) / 10,
        y: Math.round(mover(e.y, dy, t.alto, m.cajaAlto) * 10) / 10,
    };
}

/**
 * La imagen ENTERA respecto de su caja, en píxeles (izquierda, arriba, ancho, alto): lo que el modo
 * encuadre del lienzo enseña en fantasma por fuera de la caja. Misma cuenta que `focoDesdeArrastre`:
 * el borde izquierdo (p = 0) cae en `X·C − W·z·X`.
 */
export function rectoCompleto(e, m) {
    var t = tamanoPintado(m);
    if (!t) return null;
    var z = e.z / 100;
    var x = e.x / 100;
    var y = e.y / 100;
    return {
        izq: x * m.cajaAncho - t.ancho * z * x,
        arriba: y * m.cajaAlto - t.alto * z * y,
        ancho: t.ancho * z,
        alto: t.alto * z,
    };
}

/** El zoom tras un paso de rueda (`deltaY` < 0 amplía), acotado y en enteros. */
export function zoomDesdeRueda(z, deltaY) {
    var n = (z || ZOOM_MIN) * Math.exp(-deltaY * 0.0015);
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n)));
}

/** Los cambios de props de un encuadre (formato de `guardarPropsDeBloque`; el centrado sin zoom = borrar). */
export function cambiosDeEncuadre(e) {
    if (!e || (Math.round(e.x) === 50 && Math.round(e.y) === 50 && e.z <= ZOOM_MIN)) {
        return { image_pos_x: null, image_pos_y: null, image_zoom: null };
    }
    return {
        image_pos_x: Math.round(e.x),
        image_pos_y: Math.round(e.y),
        image_zoom: e.z > ZOOM_MIN ? Math.round(e.z) : null,
    };
}

// ═══════════════════════════════════════════════════════════════
//  QUÉ SE ENCUADRA (D-1452, 26-10-2026)
// ═══════════════════════════════════════════════════════════════

/**
 * Bloques con una foto POR ITEM que se encuadra: tipo → el array de props y la <img> del item,
 * dentro de su `.sortable-item[data-array-name][data-item-index]` (lo pintan los tres renders).
 * Por selector y no por una clase nueva a propósito: añadir una clase a la <img> cambiaría lo
 * publicado de todos los sitios aunque nadie encuadrase nada.
 */
export var ENCUADRE_POR_ITEM = {
    gallery: { array: 'gallery_items', img: 'img.gallery-img' },
    team: { array: 'members', img: 'img' },
    carrusel: { array: 'slides', img: 'img.carrusel-img' },
};

/** ¿Se encuadra la imagen ÚNICA del bloque? Las mismas que admiten el recorte (content,
 *  features, hero v3 e imagen). Las de los items van por `admiteEncuadreDeItem`. */
export function admiteEncuadre(tipo, props) {
    return admiteRecorte(tipo, props);
}

/** La <img> de la imagen única del bloque, dentro de su sección. */
export function selectorImagenUnica(tipo) {
    return tipo === 'imagen' ? 'img.imagen-img' : 'img.unsplash-target';
}

/** ¿Se encuadra el item `item` del array `array` de un bloque `tipo`? (Un item que no es un
 *  objeto —un string suelto del LLM— no tiene dónde guardar el encuadre.) */
export function admiteEncuadreDeItem(tipo, array, item) {
    if (!Object.prototype.hasOwnProperty.call(ENCUADRE_POR_ITEM, tipo)) return false;
    if (ENCUADRE_POR_ITEM[tipo].array !== array) return false;
    return !!item && typeof item === 'object' && !Array.isArray(item);
}
